import { useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Outlet, useNavigate, NavLink } from "react-router-dom";
import { Monitor } from "lucide-react";
import DashboardNav from "@/components/navigation/DashboardNav";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { TosConsentModal } from "@/components/modals/TosConsentModal";
import { NAV_ITEMS } from "@/constants/navigation";
import { BACKEND_URL } from "@/constants/api";
import { LEGAL_VERSIONS } from "@/constants/legal";
import { SchoolProvider } from "@/context/SchoolContext";

function localStorageKey(userId) {
  return `tos_${userId}`;
}

export default function DashboardLayout() {
  const { userId, isLoaded, getToken } = useAuth();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [tosOk, setTosOk] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (isLoaded && !userId) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, userId]);

  useEffect(() => {
    if (!isLoaded || !userId) return;

    // Fast path: localStorage already records acceptance of the current version.
    if (localStorage.getItem(localStorageKey(userId)) === LEGAL_VERSIONS.tos) {
      setTosOk(true);
      setChecking(false);
      return;
    }

    // Slow path: first visit on this device, or version was bumped.
    // One API call to check what version the DB has.
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("fetch failed");
        const user = await res.json();
        if (!cancelled) {
          if (user.tosVersion === LEGAL_VERSIONS.tos) {
            // Already accepted - cache it so we never fetch again on this device.
            localStorage.setItem(localStorageKey(userId), LEGAL_VERSIONS.tos);
            setTosOk(true);
          } else {
            setTosOk(false);
          }
        }
      } catch {
        // On error, show the modal - consent should never be silently skipped.
        if (!cancelled) setTosOk(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isLoaded, userId, getToken]);

  if (isMobile) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
        <Monitor size={48} className="text-muted-foreground" />
        <h2 className="text-xl font-semibold">Desktop Required</h2>
        <p className="text-muted-foreground text-sm max-w-xs">
          EasyReports is designed for desktop use. Please open it on a computer to access your dashboard.
        </p>
      </div>
    );
  }

  if (!isLoaded || !userId || checking) {
    return (
      <div className="h-[50vh] flex items-center justify-center">
        <div className="w-7 h-7 rounded-full border-[2.5px] border-border border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <SchoolProvider>
      <div className="flex flex-col min-h-screen">
        {/* Top Nav */}
        <DashboardNav />

        {/* Main Content */}
        <main className="flex-1 min-w-0 max-md:pb-16">
          <ErrorBoundary>
            {tosOk ? (
              <Outlet />
            ) : (
              <TosConsentModal
                onAccepted={() => {
                  localStorage.setItem(localStorageKey(userId), LEGAL_VERSIONS.tos);
                  setTosOk(true);
                }}
              />
            )}
          </ErrorBoundary>
        </main>

        {/* Mobile Bottom Nav - shares NAV_ITEMS with DashboardNav, cannot diverge */}
        <nav className="fixed bottom-0 left-0 right-0 h-[62px] bg-background border-t border-border flex items-center justify-around z-50 px-1 md:hidden">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/dashboard"}
              className={({ isActive }) =>
                `flex flex-col items-center gap-[0.2rem] px-[0.6rem] py-[0.4rem] rounded-lg text-[0.625rem] font-medium no-underline transition-colors min-w-[48px] text-center ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <item.icon size={20} />
              <span>{item.short}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </SchoolProvider>
  );
}
